/**
 * Tiny haptics wrapper.
 *
 * `navigator.vibrate` is the only cross-platform way to trigger haptics from
 * the web. It's a no-op on Safari/iOS, which is fine — patterns are designed
 * as a tactile echo of an existing visual action, never the only signal.
 *
 * Patterns are intentionally short and gentle, and none of them is the only
 * signal for anything — every one echoes something already on screen.
 *
 * They are deliberately *not* gated on `prefers-reduced-motion`. That setting
 * exists for vestibular triggers from visual motion, and a buzz isn't visual;
 * borrowing it here silenced the one channel that works when you aren't looking
 * at the screen, for exactly the people most likely to be relying on it. There
 * is no `prefers-reduced-haptics` to read instead, so the honest answer is to
 * keep every pattern short enough that none of them needs an opt-out.
 *
 * Screens rarely call this directly: the pressable primitives in `ms.tsx` each
 * carry a default pattern, so a control buzzes because of what it *is*. Reach
 * for a named export here only for something that isn't a press — a result
 * landing, or the server handing you the round.
 */

const supported =
    typeof navigator !== "undefined" &&
    typeof navigator.vibrate === "function";

function buzz(pattern: number | number[]) {
    if (!supported) return;
    try { navigator.vibrate(pattern); } catch { /* no-op */ }
}

/**
 * The vocabulary.
 *
 * Seven marks, ordered by weight. The gaps between them are the point — two
 * patterns that feel alike are one pattern with two names, and the hand can't
 * tell you which action it just took.
 */
const patterns = {
    /** A single light tap — picking up a word tile, a switch, a back button. */
    tap: 8,
    /** A short rasp — a gesture abandoned. Lighter than `tap`: nothing happened. */
    tick: 6,
    /** A double pip — committing something soft (cast vote, pick an article). */
    select: [10, 30, 10],
    /** A confident thud — the primary move on a screen. */
    submit: [20, 40, 20],
    /** Two heavy, tight knocks — an irreversible confirm. Deliberately blunter
     *  than `submit`: ending everyone's round shouldn't feel like sending your
     *  own answer. */
    destructive: [45, 35, 45],
    /** A celebratory burst — winner / champion reveal. */
    celebrate: [18, 60, 12, 40, 24],
    /**
     * The summons: two raps and a held buzz.
     *
     * The only pattern the *server* fires rather than your own thumb, and the
     * only one that has to work through a pocket or a face-down phone on a
     * table — this is a tabletop game, and by the time it's your turn to scan a
     * card nobody is watching their screen. So it's the longest by a wide
     * margin and the only one that ends on a sustained pulse: everything else
     * is a click or a burst that decays, this one lands and stays.
     */
    yourTurn: [45, 65, 45, 65, 180],
} as const satisfies Record<string, number | readonly number[]>;

export type HapticName = keyof typeof patterns;

/**
 * Fire a pattern by name. `null`/`undefined` is a no-op, so a control can pass
 * its resolved default straight through without a guard.
 */
export function fire(name: HapticName | null | undefined) {
    if (!name) return;
    const pattern = patterns[name];
    buzz(typeof pattern === "number" ? pattern : [...pattern]);
}

export const tap = () => fire("tap");
export const tick = () => fire("tick");
export const select = () => fire("select");
export const submit = () => fire("submit");
export const destructive = () => fire("destructive");
export const celebrate = () => fire("celebrate");
export const yourTurn = () => fire("yourTurn");
